import React, { createContext, useState, useContext, useEffect } from 'react';

const CustomerAuthContext = createContext();

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const storedToken = localStorage.getItem('barkbacks_customer_token');
        const storedUser = localStorage.getItem('barkbacks_customer_user');

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));

          // Verify token is still valid
          const response = await fetch(`${API_URL}/api/customer/auth/profile`, {
            headers: {
              Authorization: `Bearer ${storedToken}`,
            },
          });

          if (!response.ok) {
            // Token is invalid, clear everything
            logout();
          }
        }
      } catch (error) {
        console.error('Error loading customer user:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [API_URL]);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/customer/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Save to state and localStorage
      setToken(data.token);
      setUser(data.customer);
      localStorage.setItem('barkbacks_customer_token', data.token);
      localStorage.setItem('barkbacks_customer_user', JSON.stringify(data.customer));

      return { success: true };
    } catch (error) {
      console.error('Customer login error:', error);
      return { success: false, error: error.message };
    }
  };

  const register = async (name, email, password, phone = '') => {
    try {
      const response = await fetch(`${API_URL}/api/customer/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, password, phone }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Auto-login after registration
      setToken(data.token);
      setUser(data.customer);
      localStorage.setItem('barkbacks_customer_token', data.token);
      localStorage.setItem('barkbacks_customer_user', JSON.stringify(data.customer));

      return { success: true };
    } catch (error) {
      console.error('Customer registration error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('barkbacks_customer_token');
    localStorage.removeItem('barkbacks_customer_user');
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!token && !!user,
    API_URL,
  };

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
};

export default CustomerAuthContext;
